import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { fromEvent, Subject, takeUntil } from 'rxjs';

export interface SimpleDropdownOption {
    value: any;
    string: string;
}

export enum DropdownType {
    Default = 'default',
    Checkbox = 'checkbox',
    Radio = 'radio',
}

@Component({
    selector: 'app-simple-dropdown',
    templateUrl: './simple-dropdown.component.html',
    styleUrl: './simple-dropdown.component.css',
    standalone: false,
})
export class SimpleDropdownComponent implements OnInit, OnDestroy {
    readonly CHECKBOX = DropdownType.Checkbox;
    readonly RADIO = DropdownType.Radio;
    readonly DEFAULT = DropdownType.Default;

    @Input() options: SimpleDropdownOption[] = [];
    @Input() selectedOption: any = null;
    @Input() selectedOptions: any[] = [];

    @Input() borderless: boolean = false;
    @Input() width: number;
    @Input() isDisabled: boolean = false;
    @Input() legend: string = '';
    @Input() type: DropdownType = DropdownType.Default;
    @Input() smaller: boolean = false;
    @Output() onOptionSelected = new EventEmitter<SimpleDropdownOption>();
    @Output() onOptionsSelected = new EventEmitter<any[]>();

    @ViewChild('selElement') selElement: ElementRef;
    @ViewChild('selectorElement') selectorElement: ElementRef;

    isOpen: boolean = false;

    optionsTop: string;
    optionsLeft: string;

    private destroy$: Subject<void> = new Subject();

    constructor(
        private cdr: ChangeDetectorRef,
        private eRef: ElementRef
    ) {}

    @HostListener('window:resize', ['$event'])
    onResize(event: Event): void {
        this.updatePosition();
    }

    @HostListener('window:scroll', ['$event'])
    onScroll(event: Event): void {
        if (!this.isOpen) {
            return;
        }

        this.isOpen = false;
    }

    @HostListener('document:click', ['$event'])
    clickOut(event: MouseEvent): void {
        if (!this.isOpen) {
            return;
        }

        const target = event.target as Node;
        const clickedOnDropdown =
            this.selElement?.nativeElement.contains(target) || this.selectorElement?.nativeElement.contains(target);

        if (clickedOnDropdown) {
            if (this.type === DropdownType.Checkbox) {
                return;
            }
            return;
        }
        this.isOpen = false;
    }

    ngOnInit(): void {
        let scrollContainer: HTMLElement | null = this.eRef.nativeElement.parentElement;
        while (scrollContainer && scrollContainer !== document.body) {
            const { overflowY, overflow } = window.getComputedStyle(scrollContainer);
            if (/(auto|scroll)/.test(overflowY + overflow)) {
                break;
            }
            scrollContainer = scrollContainer.parentElement;
        }

        if (scrollContainer && scrollContainer !== document.body) {
            fromEvent(scrollContainer, 'scroll')
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    if (this.isOpen) {
                        this.isOpen = false;
                    }
                });
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['isDisabled']) {
            this.isOpen = false;
        }
    }

    get selectionMode(): DropdownType {
        return this.type;
    }

    get hasSelectedOption(): boolean {
        if (this.selectionMode === DropdownType.Checkbox) {
            return this.selectedOptions.length > 0;
        }

        return this.selectedOption !== null && this.selectedOption !== undefined;
    }

    get selectedText(): string {
        if (this.type === DropdownType.Checkbox) {
            if (!this.selectedOptions?.length) {
                return 'dlg.select';
            }

            const firstOption = this.options?.find(option => option.value === this.selectedOptions[0]);

            if (!firstOption) {
                return 'dlg.select';
            }

            const extraCount = this.selectedOptions.length - 1;
            return extraCount > 0 ? `${firstOption.string} (+${extraCount})` : firstOption.string;
        }

        if (!this.options?.length || this.selectedOption == null) {
            return 'dlg.select';
        }

        const selected = this.options.find(option => option.value === this.selectedOption);

        return selected?.string ?? 'dlg.select';
    }

    isOptionSelected(option: SimpleDropdownOption): boolean {
        if (this.selectionMode === DropdownType.Checkbox) {
            return this.selectedOptions.includes(option.value);
        }

        return this.selectedOption === option.value;
    }

    openSelector(): void {
        if (this.isDisabled) {
            return;
        }

        this.isOpen = !this.isOpen;

        if (this.isOpen) {
            this.updatePosition();
        }
    }

    onSelect(option: SimpleDropdownOption): void {
        if (this.isDisabled) {
            return;
        }

        if (this.selectionMode === DropdownType.Checkbox) {
            this.toggleCheckboxOption(option);
            return;
        }

        this.selectedOption = option.value;
        this.onOptionSelected.emit(option);
        this.isOpen = false;
    }

    private toggleCheckboxOption(option: SimpleDropdownOption): void {
        const isSelected = this.selectedOptions.includes(option.value);

        const updatedOptions = isSelected
            ? this.selectedOptions.filter(value => value !== option.value)
            : [...this.selectedOptions, option.value];

        this.selectedOptions = updatedOptions;

        this.onOptionsSelected.emit([...updatedOptions]);
    }

    private updatePosition(): void {
        if (!this.selElement || !this.isOpen) {
            return;
        }

        const selectorPosition = this.selElement.nativeElement.getBoundingClientRect();

        const scrollTop = window.scrollY;
        const scrollLeft = window.scrollX;

        const top = selectorPosition.bottom + scrollTop;

        this.optionsTop = top + 1 + 'px';
        this.optionsLeft = selectorPosition.left + scrollLeft + 'px';

        this.cdr.detectChanges();
    }
}
